// WebDAV locking (browserdav-nephele-parity Phase 3, decision 3):
// Nephele-level semantics — locks persist in IndexedDB so they survive page
// reload with continued timeout bookkeeping (absolute expiresAt), coverage is
// segment-safe, write-through requires lock-token submission via the If
// header, and 423 responses carry RFC 4918 <D:lock-token-submitted> bodies.
// A LockManager is one browser tab's lock table by construction; persistence
// guards against reloads, not concurrent instances.

import { createKVStore } from './persistence.js'

export class LockManager {
    constructor() {
        this.locks = new Map()
        this._store = createKVStore('BrowserDAV-webdav-state', 'locks')
        this._loaded = null
    }

    ensureLoaded() {
        if (!this._loaded) {
            this._loaded = (async () => {
                try {
                    for (const [token, lock] of await this._store.entries()) {
                        this.locks.set(token, lock)
                    }
                } catch (error) {
                    console.warn('LockManager: could not hydrate persisted locks:', error)
                }
                this.cleanupExpiredLocks()
            })()
        }
        return this._loaded
    }

    generateLockToken() {
        const uuid =
            typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).substring(2)}`
        return `urn:uuid:${uuid}`
    }

    _persist(token, lock) {
        this._store.set(token, lock).catch((e) => console.warn('LockManager persist failed:', e))
    }

    _unpersist(token) {
        this._store.remove(token).catch((e) => console.warn('LockManager unpersist failed:', e))
    }

    cleanupExpiredLocks() {
        const now = Date.now()
        for (const [token, lock] of this.locks.entries()) {
            if (lock.expiresAt <= now) {
                this.locks.delete(token)
                this._unpersist(token)
            }
        }
    }

    _covers(lock, path) {
        // The lock protects `path` when it locks that exact resource, or path
        // is inside an infinite-depth-locked collection.
        if (lock.path === path) return true
        if (lock.depth === 'infinity' && path.startsWith(lock.path + '/')) return true
        return false
    }

    _affectsWrite(lock, path) {
        // Writing `path` also conflicts when a locked resource lives below it
        // (deleting/moving an ancestor of a locked resource).
        return this._covers(lock, path) || lock.path.startsWith(path + '/')
    }

    /**
     * Tokens of locks currently held on this resource (for If evaluation).
     */
    tokensCovering(path) {
        this.cleanupExpiredLocks()
        const tokens = new Set()
        for (const [token, lock] of this.locks.entries()) {
            if (this._covers(lock, path)) tokens.add(token)
        }
        return tokens
    }

    /**
     * Lock gate for write methods. Returns null when writable, else a full
     * 423 response with the RFC 4918 lock-token-submitted error body.
     */
    checkWritable(path, submittedTokens = new Set()) {
        this.cleanupExpiredLocks()
        for (const [token, lock] of this.locks.entries()) {
            if (!this._affectsWrite(lock, path)) continue
            if (lock.lockscope === 'shared') continue // shared locks don't exclude writers in this model
            if (submittedTokens.has(token)) continue
            return {
                status: 423,
                headers: { 'Content-Type': 'application/xml; charset=utf-8' },
                body: `<?xml version="1.0" encoding="utf-8"?>
<D:error xmlns:D="DAV:">
    <D:lock-token-submitted>
        <D:href>${lock.path}</D:href>
    </D:lock-token-submitted>
</D:error>`,
            }
        }
        return null
    }

    isResourceLocked(path, excludeToken = null) {
        this.cleanupExpiredLocks()
        for (const [token, lock] of this.locks.entries()) {
            if (excludeToken && token === excludeToken) continue
            if (this._affectsWrite(lock, path)) {
                return { locked: true, token, lock }
            }
        }
        return { locked: false }
    }

    parseLockInfo(xmlBody) {
        const lockInfo = {
            lockscope: 'exclusive',
            locktype: 'write',
            owner: null,
        }

        if (!xmlBody || typeof xmlBody !== 'string') {
            return lockInfo
        }

        if (xmlBody.includes('<D:shared/>') || xmlBody.includes('<shared/>')) {
            lockInfo.lockscope = 'shared'
        }

        const ownerMatch =
            xmlBody.match(/<D:owner>(.*?)<\/D:owner>/s) ||
            xmlBody.match(/<owner>(.*?)<\/owner>/s)
        if (ownerMatch) {
            lockInfo.owner = ownerMatch[1].trim()
        }

        return lockInfo
    }

    parseTimeout(timeoutHeader) {
        if (!timeoutHeader) return 300

        const match = timeoutHeader.match(/Second-(\d+)/i)
        if (match) {
            return Math.min(parseInt(match[1]), 3600)
        }
        return 300
    }

    /**
     * Create or refresh a lock. `submittedTokens` comes from the request's
     * If header: a bodyless LOCK submitting a covering lock's token is a
     * refresh (RFC 4918 §9.10.2).
     */
    createLock(path, body, headers = {}, submittedTokens = new Set()) {
        this.cleanupExpiredLocks()
        const depth = headers.depth === 'infinity' ? 'infinity' : '0'
        const timeoutSeconds = this.parseTimeout(headers.timeout)

        const hasBody = body && typeof body === 'string' && body.trim().length > 0
        if (!hasBody && submittedTokens.size > 0) {
            for (const token of submittedTokens) {
                const lock = this.locks.get(token)
                if (lock && this._covers(lock, path)) {
                    lock.expiresAt = Date.now() + timeoutSeconds * 1000
                    lock.timeout = `Second-${timeoutSeconds}`
                    this._persist(token, lock)
                    return { token, lock, refreshed: true }
                }
            }
            // RFC 4918 §9.10.6 precondition DAV:lock-token-matches-request-uri
            throw {
                status: 412,
                message: 'No covering lock for the submitted token to refresh',
                headers: { 'Content-Type': 'application/xml; charset=utf-8' },
                body: `<?xml version="1.0" encoding="utf-8"?>
<D:error xmlns:D="DAV:">
    <D:lock-token-matches-request-uri/>
</D:error>`,
            }
        }

        const lockCheck = this.isResourceLocked(path)
        if (lockCheck.locked && !submittedTokens.has(lockCheck.token)) {
            // RFC 4918 §16 precondition DAV:no-conflicting-lock — a LOCK
            // refused because another lock already covers the resource.
            throw {
                status: 423,
                message: 'Resource is locked',
                lockToken: lockCheck.token,
                headers: { 'Content-Type': 'application/xml; charset=utf-8' },
                body: `<?xml version="1.0" encoding="utf-8"?>
<D:error xmlns:D="DAV:">
    <D:no-conflicting-lock>
        <D:href>${lockCheck.lock.path}</D:href>
    </D:no-conflicting-lock>
</D:error>`,
            }
        }

        const lockInfo = this.parseLockInfo(hasBody ? body : null)
        const token = this.generateLockToken()
        const lock = {
            path: path,
            token: token,
            lockscope: lockInfo.lockscope,
            locktype: lockInfo.locktype,
            owner: lockInfo.owner,
            depth: depth,
            timeout: `Second-${timeoutSeconds}`,
            expiresAt: Date.now() + timeoutSeconds * 1000,
            createdAt: Date.now(),
        }

        this.locks.set(token, lock)
        this._persist(token, lock)
        return { token, lock, refreshed: false }
    }

    removeLock(path, headers = {}) {
        const lockToken = headers['lock-token']
        if (!lockToken) {
            throw { status: 400, message: 'Missing Lock-Token header' }
        }

        const token = lockToken.replace(/[<>]/g, '')
        const lock = this.locks.get(token)

        if (!lock) {
            throw { status: 409, message: 'Lock token not found' }
        }

        if (lock.path !== path) {
            throw { status: 409, message: 'Lock token does not match resource' }
        }

        this.locks.delete(token)
        this._unpersist(token)
        return true
    }

    removeLocksForPath(path) {
        for (const [token, lock] of this.locks.entries()) {
            if (lock.path === path || lock.path.startsWith(path + '/')) {
                this.locks.delete(token)
                this._unpersist(token)
            }
        }
    }

    getLocks() {
        return this.locks
    }
}
